import { ButtonComponent, CurrenciesSelector, Loader } from "@/components";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { CURRENCIES } from "@/data/data.tsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError, ApiResponse } from "@/types/Api.tsx";
import type { FormError, ValidationErrors } from "@/types/ValidationError.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import CustomLogger from "@/utils/CustomLogger.tsx";
import type {
  CardPayment,
  CreateExchange,
  ExchangeRate,
  IBANAcc,
} from "@/types/Exchange.tsx";
import { type AxiosError, isAxiosError } from "axios";
import { formatError } from "@/utils/formatError.tsx";
import type { Currency } from "@/types/Currency.tsx";
import type { CreateTransaction, Transaction } from "@/types/Transaction.tsx";
import {
  BalanceQueryKey,
  CurrencyQueryKey,
  GetTransactionsQueryKey,
  IBANAccountQueryKey,
} from "@/config/constants/QueryKeys.tsx";
import customArrow from "@/assets/SVG/CustomArrow.svg";
import { getData, getUser } from "@/utils/localStorage.tsx";
import { useSnackBarAlert } from "@/hooks/useSnackbar.tsx";
import type { Balance } from "@/types/Balance.tsx";
import { QRCodeCanvas } from "qrcode.react";
import { useNavigate } from "react-router-dom";
import { RoutesPathList } from "@/config/routes/Routes.tsx";
import { clientPosKey } from "@/config/constants/StorageKeys.tsx";
import { isSNS } from "@/utils/utils";
import bgSNS from "../assets/SVG/bgSNS.svg";
import ArrowSNS from "../assets/SVG/ArrowSNS.svg";
import { Player } from "@lottiefiles/react-lottie-player";
import arrowSNS from "../assets/SVG/sageti3.json";
import { motion } from "framer-motion";
// import bgcyan from "../assets/SVG/cyangradient.svg";

interface SwipeToAcceptProps {
  onAccept: () => void;
  initialTime?: number;
  disabled?: boolean;
}

const SwipeToAccept: React.FC<SwipeToAcceptProps> = ({
  onAccept,
  initialTime = 59,
  disabled = false,
}) => {
  const [isCompleted, setIsCompleted] = useState(false);
  const [timeLeft, setTimeLeft] = useState(initialTime);
  const [dragPosition, setDragPosition] = useState(0);
  const sliderRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!disabled && !isCompleted && timeLeft > 0) {
      timerRef.current = setTimeout(() => {
        setTimeLeft((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [timeLeft, disabled, isCompleted]);

  useEffect(() => {
    if (timeLeft <= 0) {
      setTimeLeft(initialTime);
      setDragPosition(0);
      setIsCompleted(false);
    }
  }, [timeLeft, initialTime]);

  const maxDistance = sliderRef.current
    ? sliderRef.current.getBoundingClientRect().width - 7.9375 * 16 - 32
    : 0;

  const hidePlayer = dragPosition >= maxDistance * 0.5;

  const handleDrag = (
    _: MouseEvent | TouchEvent | PointerEvent,
    info: { point: { x: number } }
  ) => {
    if (!sliderRef.current) return;
    const rect = sliderRef.current.getBoundingClientRect();
    const thumbWidthPx = 7.9375 * 16;

    const startX = rect.right - thumbWidthPx / 2 - 16;
    let newDragPosition = startX - info.point.x;

    if (newDragPosition < 0) newDragPosition = 0;
    if (newDragPosition > maxDistance) newDragPosition = maxDistance;

    setDragPosition(newDragPosition);
  };

  const handleDragEnd = () => {
    if (dragPosition >= maxDistance * 0.98) {
      setIsCompleted(true);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      setTimeout(() => {
        onAccept();
      }, 300);
    } else {
      setDragPosition(0);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div
        ref={sliderRef}
        className={`
          relative h-16 rounded-[0.625rem] overflow-hidden select-none
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        `}
        style={{
          background: isCompleted
            ? "linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)"
            : "linear-gradient(135deg, #EC4899 0%, #8B5CF6 100%)",
        }}
      >
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white font-medium text-lg">
          {formatTime(timeLeft)}
        </div>

        <div className="absolute right-0 top-1/2 w-full pointer-events-none -translate-y-1/2">
          <motion.div
            drag="x"
            dragConstraints={sliderRef}
            dragElastic={0}
            onDrag={handleDrag}
            onDragEnd={handleDragEnd}
            className={`
              ml-auto  w-[7.9375rem] h-[3.95rem]
              bg-cyan-300 text-black px-6 py-2 rounded-[0.625rem] font-medium
              cursor-grab active:cursor-grabbing
              flex items-center justify-center select-none
              pointer-events-auto
              ${isCompleted ? "" : ""}
            `}
            style={{
              x: -dragPosition,
              transition:
                dragPosition === 0 ? "transform 0.3s ease-out" : "none",
            }}
            animate={{
              x: -dragPosition,
            }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
          >
            {isCompleted ? "Accepted!" : "Accept"}
          </motion.div>
        </div>

        {!hidePlayer && (
          <div className=" absolute right-[7.5rem] top-1/2 transform -translate-y-1/2">
            <Player
              autoplay={true}
              loop={true}
              src={arrowSNS}
              style={{
                height: "4rem",
                width: "4rem",
                transform: "rotate(90deg)",
              }}
            />
          </div>
        )}

        <div
          className="absolute top-0 right-0 h-full overflow-hidden transition-all duration-300"
          style={{
            width: `${(Math.abs(dragPosition) / maxDistance) * 100}%`,
          }}
        ></div>
      </div>
    </div>
  );
};

const ExchangePhone: React.FC = () => {
  const { showSnackBar } = useSnackBarAlert();
  const [isRotated, setIsRotated] = useState(false);
  const [err, setError] = useState<{ type: string; message: string } | null>(
    null
  );
  const [hasError, setHasError] = useState(false);
  const clientData = getData<{ client_id: number | null }>(clientPosKey);
  const [exchangeData, setExchangeData] = useState<ExchangeRate | null>(null);
  const [exchangeModal, setExchangeModal] = useState(false);
  const [timeLeft, setTimeLeft] = useState(59);
  const [exchangeStep, setExchangeStep] = useState(1);
  const ibanRef = useRef<HTMLDivElement>(null);
  const swiftRef = useRef<HTMLDivElement>(null);
  const nameAddressRef = useRef<HTMLDivElement>(null);
  const [isExchangeModalClosed, setIsExchangeModalClosed] = useState(false);
  const queryClient = useQueryClient();
  const [paymentMethod, setPaymentMethod] = useState<"card" | "bank">("card");
  const [selectedOptions, setSelectedOptions] = useState<{
    up: Currency;
    down: Currency;
  }>({
    up: CURRENCIES[2],
    down: CURRENCIES[3],
  });

  const {
    data: currencies,
    isLoading: isLoadingCurrencies,
    error: currencyError,
  } = useQuery<ApiResponse<Currency[]>>({
    queryFn: async () => GenericApi.get<Currency[]>(ApiRoutes.CURRENCY),
    queryKey: [CurrencyQueryKey],
  });

  const { data: ibanAcc, isLoading: isLoadingIbanAcc } = useQuery<
    ApiResponse<IBANAcc>
  >({
    queryFn: async () => GenericApi.get<IBANAcc>(ApiRoutes.IBANACCOUNT),
    queryKey: [IBANAccountQueryKey],
  });

  const {
    data: balance,
    isLoading: isLoadingBalance,
    error: balanceError,
  } = useQuery<ApiResponse<Balance[]>>({
    queryFn: async () => GenericApi.get<Balance[]>(ApiRoutes.GET_BALANCE),
    queryKey: [BalanceQueryKey],
  });

  const options = currencies?.data?.map((curr) => {
    return {
      code: curr.symbol ?? "",
      symbol: curr.symbol ?? "",
      name: "",
      price: 0,
      type: curr.type,
    };
  });

  const [selectedAccounts, setSelectedAccounts] = useState<{
    buy_bank_account: number | null;
    buy_crypto_account: number | null;
    sell_bank_account: number | null;
    sell_crypto_account: number | null;
  }>({
    buy_bank_account: null,
    buy_crypto_account: null,
    sell_bank_account: null,
    sell_crypto_account: null,
  });

  const createExchangeMutation = useMutation<
    ApiResponse<ExchangeRate>,
    ApiError<ValidationErrors>,
    CreateExchange,
    { inputFocusedDown: boolean }
  >({
    mutationFn: (data) =>
      GenericApi.post<ExchangeRate>(ApiRoutes.EXCHANGE, {
        ...data,
        amount: data.inputFocusedDown
          ? selectedOptions.down.price
          : selectedOptions.up.price,
      }),
    onMutate: async (variables) => {
      return { inputFocusedDown: variables.inputFocusedDown };
    },
    onSuccess: async ({ data }, _vars, context) => {
      setExchangeData(data);
      if (!data?.rate) {
        showSnackBar(
          "No exchange rate found",
          "error",
          {
            vertical: "top",
            horizontal: "right",
          },
          5000
        );
        return;
      }

      if (context?.inputFocusedDown) {
        setSelectedOptions({
          ...selectedOptions,
          up: {
            ...selectedOptions.up,
            price: Number((data.rate * selectedOptions.down.price).toFixed(5)),
          },
        });
      } else {
        setSelectedOptions({
          ...selectedOptions,
          down: {
            ...selectedOptions.down,
            price: Number((data.rate * selectedOptions.up.price).toFixed(5)),
          },
        });
      }
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
      setHasError(true);
    },
  });

  useEffect(() => {
    if (currencies?.data) {
      setSelectedOptions({
        up: {
          ...currencies?.data?.[0],
          name: "a",
          price: 0,
          code: currencies?.data?.[0]?.symbol ?? "",
        },
        down: {
          ...currencies?.data?.[4],
          name: "a",
          price: 0,
          code: currencies?.data?.[4]?.symbol ?? "",
        },
      });
    }
  }, [currencies?.data]);

  const checkTransactionMutation = useMutation<
    ApiResponse<Transaction>,
    ApiError<ValidationErrors>,
    CreateTransaction
  >({
    mutationFn: (data) =>
      GenericApi.post<Transaction>(ApiRoutes.TRANSACTION_CHECK, {
        ...data,
        client_id: clientData?.client_id,
      }),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      setIsExchangeModalClosed(true);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
    },
  });

  const [justCreatedTransaction, setJustCreatedTransaction] = useState<
    string | null
  >(null);

  const navigate = useNavigate();

  const createTransactionMutation = useMutation<
    ApiResponse<Transaction>,
    ApiError<ValidationErrors>,
    CreateTransaction
  >({
    mutationFn: (data) =>
      GenericApi.post<Transaction>(ApiRoutes.TRANSACTIONS, { ...data }),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      setJustCreatedTransaction(data?.id ?? null);
      setIsExchangeModalClosed(true);
      console.log("Selected options", selectedOptions);
      navigate(
        isSNS() ? RoutesPathList.ScanWalletSNS : RoutesPathList.ScanWallet,
        {
          state: {
            currency: selectedOptions.up.symbol,
            transaction_id: data?.id,
          },
        }
      );
      return;
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
    },
  });

  useEffect(() => {
    if (!exchangeModal && isExchangeModalClosed) {
      showSnackBar(
        "Transaction created successfully",
        "success",
        { vertical: "top", horizontal: "right" },
        5000
      );
      setIsExchangeModalClosed(false);
    }
  }, [exchangeModal, isExchangeModalClosed]);

  const handleAxiosError = (error: AxiosError<unknown> | null | Error) => {
    const errorMessage = "Something went wrong";
    if (
      isAxiosError<ValidationErrors>(error) &&
      Array.isArray(error.response?.data?.detail)
    ) {
      setError({
        type: "manual",
        message: formatError(error, errorMessage),
      });
      return;
    }

    if (isAxiosError<FormError>(error)) {
      setError({
        type: "manual",
        message: error.response?.data?.detail ?? errorMessage,
      });
      return;
    }

    setError({
      type: "manual",
      message: error?.message ?? errorMessage,
    });
  };

  const handleClick = async () => {
    setIsRotated(!isRotated);
    if (hasError) {
      setHasError(false);
      selectedOptions.up.price = 0;
      selectedOptions.down.price = 0;
      setSelectedOptions({
        ...selectedOptions,
        down: selectedOptions.up,
        up: selectedOptions.down,
      });
    }

    const updatedState = {
      ...selectedOptions,
      up: selectedOptions.down,
      down: selectedOptions.up,
    };

    setSelectedOptions(updatedState);
  };

  const onChangeInput = (
    value: number,
    index: number,
    {
      inputFocusedUp,
    }: {
      inputFocusedUp: boolean;
      inputFocusedDown: boolean;
    }
  ) => {
    if (index === 0) {
      setSelectedOptions({
        ...selectedOptions,
        up: { ...selectedOptions.up, price: value },
      });
    } else {
      setSelectedOptions({
        ...selectedOptions,
        down: { ...selectedOptions.down, price: value },
      });
    }

    setHasError(false);

    setTimeout(() => {
      if (inputFocusedUp) {
        handleCreateExchange({
          from_currency_symbol: selectedOptions.up.code,
          to_currency_symbol: selectedOptions.down.code,
          inputFocusedUp: true,
          inputFocusedDown: false,
        });
      } else {
        handleCreateExchange({
          from_currency_symbol: selectedOptions.down.code,
          to_currency_symbol: selectedOptions.up.code,
          inputFocusedUp: false,
          inputFocusedDown: true,
        });
      }
    }, 700);
  };

  const handleOnChangeCurrencies = (option: Currency, index: number) => {
    setHasError(false);

    const newSelectedOptions = {
      up:
        index === 0
          ? { ...option, price: 0 }
          : { ...selectedOptions.up, price: 0 },
      down:
        index === 1
          ? { ...option, price: 0 }
          : { ...selectedOptions.down, price: 0 },
    };

    setSelectedOptions(newSelectedOptions);

    const shouldCalculate =
      index === 0
        ? selectedOptions.down.price > 0
        : selectedOptions.up.price > 0;

    if (shouldCalculate) {
      handleCreateExchange({
        from_currency_symbol:
          index === 0 ? option.code : selectedOptions.up.code,
        to_currency_symbol:
          index === 1 ? option.code : selectedOptions.down.code,
        inputFocusedUp: index === 1,
        inputFocusedDown: index === 0,
      });
    }
  };

  const handleCreateExchange = (data: CreateExchange) => {
    setError(null);
    createExchangeMutation.mutate(data);
  };

  const handleCreateTransaction = async (data: CreateTransaction) => {
    if (selectedOptions?.up?.type === "fiat") {
      navigate(RoutesPathList.TransactionApproved, {
        state: {
          exchangeId: exchangeData?.id,
          cashInCase: selectedOptions?.up?.type === "fiat",
          isComingFromTransactionApproved: false,
          txData: {
            rate_id: exchangeData?.id,
            amount: selectedOptions.up.price,
            buy_bank_account_id: selectedAccounts.buy_bank_account,
            buy_crypto_account_id: selectedAccounts.buy_crypto_account,
            sell_bank_account_id: selectedAccounts.sell_bank_account,
            sell_crypto_account_id: selectedAccounts.sell_crypto_account,
            client_id: clientData?.client_id ?? null,
          },
        },
      });
      return;
    }

    checkTransactionMutation.mutate(data, {
      onSuccess: async () => {
        createTransactionMutation.mutate(data, {
          onSuccess: async () => {
            queryClient.invalidateQueries({
              queryKey: [GetTransactionsQueryKey],
            });
          },
        });
      },
      onError: async ({ error }) => {
        CustomLogger.error(error);
        handleAxiosError(error);
      },
    });
  };

  const user = getUser();
  const fullName = `${
    user?.business_info?.business_name || user?.business_name || "N/A"
  }`;

  const [copiedIban, setCopiedIban] = useState(false);
  const [copiedSwift, setCopiedSwift] = useState(false);
  const [copiedNameAddress, setCopiedNameAddress] = useState(false);

  const copyIbanToClipboard = () => {
    if (ibanRef.current) {
      navigator.clipboard.writeText(ibanRef.current.innerText).then(() => {
        setCopiedIban(true);
        setTimeout(() => setCopiedIban(false), 2000);
      });
    }
  };

  const copySwiftToClipboard = () => {
    if (swiftRef.current) {
      navigator.clipboard.writeText(swiftRef.current.innerText).then(() => {
        setCopiedSwift(true);
        setTimeout(() => setCopiedSwift(false), 2000);
      });
    }
  };

  const copyNameAddressToClipboard = () => {
    if (nameAddressRef.current) {
      navigator.clipboard
        .writeText(nameAddressRef.current.innerText)
        .then(() => {
          setCopiedNameAddress(true);
          setTimeout(() => setCopiedNameAddress(false), 2000);
        });
    }
  };

  useEffect(() => {
    if (!exchangeModal && exchangeStep != 1) return;

    setTimeLeft(59);

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (exchangeStep === 1) {
            setExchangeModal(false);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [exchangeModal, exchangeStep]);

  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");

  const postCardPayment = useMutation<
    ApiResponse<Transaction>,
    ApiError<ValidationErrors>,
    CardPayment
  >({
    mutationFn: (data) =>
      GenericApi.post<Transaction>(ApiRoutes.CARD_PAYMENT, data),
    onSuccess: async ({ data }) => {
      CustomLogger.log(data);
      setIsExchangeModalClosed(true);
    },
    onError: async ({ error }) => {
      CustomLogger.error(error);
      handleAxiosError(error);
    },
  });

  const handlePostCardPayment = (data: CardPayment) => {
    data.payment_channel = "PCL_CARD";
    if (justCreatedTransaction) {
      data.transaction_id = justCreatedTransaction;
    } else {
      throw new Error("Transaction ID is null");
    }

    data.card = {
      number: cardNumber,
      expiry_date: expiryDate.replace(/\//g, ""),
      cvv: cvv,
    };

    postCardPayment.mutate(data);
    setExchangeStep(3);
  };

  // Handle swipe accept
  const handleSwipeAccept = () => {
    if (!exchangeData) return;

    handleCreateTransaction({
      rate_id: exchangeData?.id,
      amount: selectedOptions.up.price,
      buy_bank_account_id: selectedAccounts.buy_bank_account,
      buy_crypto_account_id: selectedAccounts.buy_crypto_account,
      sell_bank_account_id: selectedAccounts.sell_bank_account,
      sell_crypto_account_id: selectedAccounts.sell_crypto_account,
      client_id: clientData?.client_id ?? null,
    });

    setSelectedAccounts({
      buy_bank_account: null,
      buy_crypto_account: null,
      sell_bank_account: null,
      sell_crypto_account: null,
    });
  };

  if (isLoadingCurrencies || isLoadingBalance || isLoadingIbanAcc) {
    return <Loader />;
  }

  if (
    currencyError ||
    !currencies ||
    currencies?.error ||
    !currencies?.data ||
    balanceError ||
    !balance ||
    balance?.error
  ) {
    return (
      <div>Error: {currencies?.error?.message ?? "Something went wrong"}</div>
    );
  }

  console.log(options);

  return !isSNS() ? (
    <div className="exchange-phone">
      <div className="exchange-phone__right">
        <div className="exchange-phone__right__top">
          <div className="exchange-phone__right__top__title !text-[3.75rem] !font-[600] flex flex-col justify-center">
            <button
              className="mr-[18rem]"
              onClick={() => navigate("/pos/new-client-pos")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="26"
                viewBox="0 0 16 26"
                fill="none"
              >
                <path
                  d="M13.5 2L3 14L13.5 24"
                  stroke="#202A43"
                  stroke-width="4"
                  stroke-linecap="round"
                />
              </svg>
            </button>
            {/* <GoBack
              className={"w-full flex justify-start items-center mr-[3rem]"}
            />{" "} */}
            <div className="ml-[3rem]">Convert</div>
          </div>
          <div className="exchange-phone__right__top__account">
            <div>{fullName}</div>
          </div>
        </div>

        <div className="exchange-phone__right__container">
          <div className="exchange-phone__right__container--white">
            <div className="w-full flex-col justify-center items-center">
              <div className="exchange-phone__right__container--spacing">
                <CurrenciesSelector
                  balance={balance?.data ?? null}
                  isLoading={createExchangeMutation.isPending}
                  index={1}
                  currency={
                    !isRotated
                      ? selectedOptions.down.code
                      : selectedOptions.up.code
                  }
                  isUp={isRotated}
                  hasError={hasError}
                  value={
                    !isRotated
                      ? Number(selectedOptions.down.price.toFixed(8))
                      : selectedOptions.up.price
                  }
                  onChangeInput={onChangeInput}
                  options={options ?? []}
                  defaultOption={options?.[1] ?? CURRENCIES[3]}
                  currencie={""}
                  name={` line--pink selector ${isRotated ? "up1" : "down1"}`}
                  editable={true}
                  onChooseOption={handleOnChangeCurrencies}
                />

                <CurrenciesSelector
                  balance={balance?.data ?? null}
                  isLoading={createExchangeMutation.isPending}
                  index={0}
                  isUp={!isRotated}
                  hasError={hasError}
                  onChangeInput={onChangeInput}
                  options={options ?? []}
                  defaultOption={options?.[0] ?? CURRENCIES[2]}
                  currency={
                    !isRotated
                      ? selectedOptions.up.code
                      : selectedOptions.down.code
                  }
                  value={
                    !isRotated
                      ? selectedOptions.up.price
                      : Number(selectedOptions.down.price.toFixed(8))
                  }
                  currencie={""}
                  name={`line--bej selector ${isRotated ? "down2" : "up2"}`}
                  editable={true}
                  onChooseOption={handleOnChangeCurrencies}
                />

                <div className="absoluteCenter" onClick={handleClick}>
                  <img
                    className={isRotated ? "rotate" : "reverseRotate"}
                    src={customArrow || "/placeholder.svg"}
                    alt="customArrow"
                  />
                </div>
              </div>

              <div className="exchange-phone__right__container__stats">
                <div
                  className={`${
                    exchangeData ? "text-[#222B45]" : ""
                  } flex-space-100`}
                >
                  <div>Amount</div>
                  <div>
                    {exchangeData === null
                      ? "- -"
                      : `${selectedOptions.up.price} ${selectedOptions.up.code}`}
                  </div>
                </div>

                <div
                  className={`${
                    exchangeData ? "text-[#222B45]" : ""
                  } flex-space-100`}
                >
                  <div>Price</div>
                  <div>
                    {exchangeData === null
                      ? "- -"
                      : `1 ${selectedOptions.up.code} = ${Number(
                          exchangeData?.rate ?? 0
                        ).toFixed(10)} ${selectedOptions.down.code}`}
                  </div>
                </div>

                <div
                  className={`${
                    exchangeData ? "text-[#222B45]" : ""
                  } flex-space-100`}
                >
                  <div>Exchange</div>
                  <div>
                    {exchangeData === null
                      ? "- -"
                      : `${Number(selectedOptions.down.price ?? 0).toFixed(
                          10
                        )} ${selectedOptions.down.code}`}
                  </div>
                </div>

                <div
                  className={`${
                    exchangeData ? "text-[#222B45]" : ""
                  } flex-space-100`}
                >
                  <div>Fee</div>
                  <div>
                    {exchangeData === null
                      ? "- -"
                      : `${Number(
                          exchangeData?.fee.amount_percentage ?? 0
                        ).toFixed(2)}%`}
                  </div>
                </div>

                <div
                  className={`${
                    exchangeData ? "text-[#222B45]" : ""
                  } flex-space-100`}
                >
                  <div>Estimated Total</div>
                  <div>
                    {exchangeData === null ? "- -" : exchangeData?.total}
                  </div>
                </div>
              </div>

              <div className="exchange-phone__right__container__button">
                <div className={`${!err ? "hidden" : ""} error text-red-500`}>
                  {err?.message ? err.message : "Something went wrong"}
                </div>

                <ButtonComponent
                  title={
                    createTransactionMutation.isPending
                      ? "Loading..."
                      : "Exchange"
                  }
                  actionCallback={() => {
                    if (
                      selectedOptions?.up?.type === "crypto" &&
                      selectedOptions?.down?.type === "crypto"
                    ) {
                      showSnackBar(
                        "You can't exchange two cryptocurrencies",
                        "error",
                        {
                          vertical: "top",
                          horizontal: "right",
                        },
                        5000
                      );
                      return;
                    }
                    setExchangeModal(true);
                    setExchangeStep(1);
                  }}
                  name={`button--classic !bg-[#222b45] !text-white ${
                    selectedOptions?.down?.type === "fiat" &&
                    selectedAccounts.buy_bank_account === null
                      ? "cursor-not-allowed"
                      : ""
                  }
                      ${
                        selectedOptions?.up?.type === "crypto" &&
                        selectedOptions?.down?.type === "crypto"
                          ? "cursor-not-allowed"
                          : ""
                      }`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {exchangeModal ? (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full">
          <div className="min-w-[30rem] h-auto pb-[2rem] bg-white rounded-[1.5rem] border text-center relative px-8 py-8">
            {exchangeStep === 1 ? (
              <div className="flex flex-col justify-center items-center">
                {" "}
                <div className="text-[#05004E] pt-[2.5rem] text-center text-[1.5rem] font-[700]">
                  Confirm Exchange
                </div>
                <div className="text-[#737791] text-[1.2rem] w-full pt-[1rem] font-[500] text-center flex justify-center items-center gap-2">
                  The price will be recalculated in{" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_2348_4001)">
                      <path
                        d="M9.5 0C4.25326 0 0 4.25355 0 9.50018C0 14.7468 4.25326 19 9.5 19C14.7467 19 19 14.7468 19 9.50018C19 4.25355 14.7467 0 9.5 0ZM13.9057 12.3941C13.6774 12.7417 13.2977 12.9302 12.9108 12.9302C12.6868 12.9302 12.4604 12.8671 12.2594 12.7347L8.31104 10.1417V4.0143C8.31104 3.35747 8.84352 2.82536 9.5 2.82536C10.1565 2.82536 10.689 3.35747 10.689 4.0143V8.85867L13.5647 10.7475C14.1136 11.1082 14.266 11.8452 13.9057 12.3941Z"
                        fill="#737791"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_2348_4001">
                        <rect width="19" height="19" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>{" "}
                  00:
                  {timeLeft < 10 ? `0${timeLeft}` : timeLeft}
                </div>
                <ButtonComponent
                  title={
                    createTransactionMutation.isPending
                      ? "Loading..."
                      : "Accept & Pay"
                  }
                  actionCallback={() => {
                    if (!exchangeData) return;

                    handleCreateTransaction({
                      rate_id: exchangeData?.id,
                      amount: selectedOptions.up.price,
                      buy_bank_account_id: selectedAccounts.buy_bank_account,
                      buy_crypto_account_id:
                        selectedAccounts.buy_crypto_account,
                      sell_bank_account_id:
                        selectedAccounts.sell_bank_account,
                      sell_crypto_account_id:
                        selectedAccounts.sell_crypto_account,
                      client_id: clientData?.client_id ?? null,
                    });
                    setSelectedAccounts({
                      buy_bank_account: null,
                      buy_crypto_account: null,
                      sell_bank_account: null,
                      sell_crypto_account: null,
                    });
                  }}
                  name="button--classic w-fit mt-[4rem]"
                />
                <div
                  className="flex justify-center items-center gap-2 mt-4 cursor-pointer text-[#F00]"
                  onClick={() => setExchangeModal(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                  >
                    <path
                      d="M15.8026 14.0668C16.4956 15.2312 15.2312 16.4956 14.0668 15.8026L9.53101 11.2822L9.4217 11.3148L4.93315 15.8026C3.76877 16.4956 2.50437 15.2312 3.19743 14.0668L7.71776 9.53101L7.6852 9.4217L3.34782 5.09207C2.65244 4.37654 3.11448 3.17882 4.06335 3.01835C4.4153 2.95866 4.78043 3.08889 5.05099 3.31138L9.46899 7.71776L9.5783 7.68598L14.0668 3.19743C15.2312 2.50437 16.4956 3.76877 15.8026 4.93315L11.2822 9.46899L11.314 9.5783L15.8026 14.0668Z"
                      fill="#FF0000"
                    />
                  </svg>
                  Close transaction
                </div>{" "}
              </div>
            ) : null}

            {exchangeStep === 2 ? (
              <div className="flex flex-col justify-center items-center">
                <div className="flex gap-4">
                  <div
                    onClick={() => setPaymentMethod("card")}
                    className="flex flex-col justify-start items-start px-8 py-8 w-full border border-[#202A43] rounded-[1.5rem] relative"
                  >
                    {paymentMethod == "bank" && (
                      <div className="absolute w-full h-full top-0 left-0 bg-[#fff] opacity-75 rounded-[1.5rem]"></div>
                    )}

                    <div className="flex justify-between items-center w-full">
                      <div className="text-[#202A43] font-satoshi text-[1.5rem] font-bold leading-normal">
                        Pay by Card
                      </div>
                      <div className="border border-[#C0C0C0] rounded-[50%] px-[2px] py-[2px]">
                        <div
                          className={`w-4 h-4 rounded-[50%] ${
                            paymentMethod == "card" ? "bg-[#202A43]" : ""
                          } `}
                        ></div>
                      </div>
                    </div>

                    <form
                      className="h-full flex flex-col justify-center space-y-4"
                      autoComplete="on"
                    >
                      <div>
                        <input
                          type="text"
                          placeholder="Card Number"
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          autoComplete="cc-number"
                          value={cardNumber}
                          onChange={(e) => setCardNumber(e.target.value)}
                          required={paymentMethod == "card"}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Name"
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          autoComplete="cc-name"
                          required={paymentMethod == "card"}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="MM/YY"
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          autoComplete="cc-exp"
                          pattern="^(0[1-9]|1[0-2])\/[0-9]{2}$"
                          maxLength={5}
                          value={expiryDate}
                          onChange={(e) => {
                            const value = e.target.value.replace(
                              /[^0-9/]/g,
                              ""
                            );
                            if (value.length === 2 && !value.includes("/")) {
                              setExpiryDate(value + "/");
                            } else {
                              setExpiryDate(value);
                            }
                          }}
                          required={paymentMethod == "card"}
                        />
                        <input
                          type="text"
                          placeholder="CVV"
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          autoComplete="cc-csc"
                          value={cvv}
                          onChange={(e) => setCvv(e.target.value)}
                          required={paymentMethod == "card"}
                        />
                      </div>
                    </form>
                  </div>

                  <div
                    onClick={() => setPaymentMethod("bank")}
                    className="flex flex-col justify-center items-start px-8 py-8 w-full border border-[#202A43] rounded-[1.5rem] relative"
                  >
                    {paymentMethod == "card" && (
                      <div className="absolute w-full h-full top-0 left-0 bg-[#fff] opacity-75 rounded-[1.5rem]"></div>
                    )}

                    <div className="flex justify-between items-center w-full  pb-[2rem]">
                      <div className="text-[#202A43] font-satoshi text-[1.5rem] font-bold leading-normal">
                        Bank Transfer
                      </div>
                      <div className="border border-[#C0C0C0] rounded-[50%] px-[2px] py-[2px]">
                        <div
                          className={`w-4 h-4 rounded-[50%] ${
                            paymentMethod == "bank" ? "bg-[#202A43]" : ""
                          } `}
                        ></div>
                      </div>
                    </div>

                    <QRCodeCanvas
                      value={ibanAcc?.data?.iban || ""}
                      size={180}
                    />

                    <div className="flex justify-start pt-8">
                      <div>
                        <div className="text-[#CFCFCF] font-satoshi text-[1.2rem] w-full pt-[1rem] font-[500] text-center flex justify-start items-center gap-2 ">
                          IBAN
                        </div>
                        <div className="flex justify-start items-center gap-2 w-full ">
                          <div
                            className="text-[#202A43] text-[1.1875rem] font-[700]  font-satoshi"
                            ref={ibanRef}
                          >
                            {copiedIban ? "Copied!" : ibanAcc?.data?.iban}
                          </div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="21.13"
                            viewBox="0 0 23 27"
                            fill="none"
                            onClick={copyIbanToClipboard}
                            className="cursor-pointer"
                          >
                            <path
                              d="M0 22.9736V7.84911C0.294512 5.94469 1.765 4.44749 3.70162 4.21367L14.4088 4.20898C16.3921 4.41989 17.9403 5.98999 18.1591 7.97722V22.7939C17.9751 24.8077 16.4045 26.3935 14.4088 26.6137H3.80013C1.91692 26.4877 0.208439 24.8473 0 22.9736ZM3.91835 6.30245C2.89015 6.44149 2.17928 7.2091 2.06728 8.23708V22.5856C2.18239 23.6563 2.94097 24.4187 4.00702 24.5349H14.1495C15.2156 24.4192 15.9741 23.6568 16.0892 22.5856V8.23708C15.9741 7.16639 15.2156 6.404 14.1495 6.28787L3.91783 6.30245H3.91835Z"
                              fill="#222B45"
                            />
                            <path
                              d="M18.6746 0C20.5713 0.294751 22.0573 1.77528 22.2927 3.71928L22.2974 19.9863C22.0817 21.2002 20.4406 21.2002 20.2249 19.9863L20.2285 4.02653C20.1129 2.9548 19.3543 2.19345 18.2888 2.07732L6.45181 2.06795C5.7902 1.89557 5.42517 1.18525 5.75079 0.55357C5.90012 0.263506 6.13604 0.138002 6.41552 0H18.6756L18.6746 0Z"
                              fill="#222B45"
                            />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <div className="text-[#CFCFCF] font-satoshi text-[1.2rem] w-full pt-[1rem] font-[500] text-center flex justify-start ps-[3rem] items-center gap-2">
                          SWIFT
                        </div>
                        <div className="flex w-full justify-start ps-[3rem] items-center gap-2">
                          <div
                            className="text-[#202A43] font-satoshi text-[1.1875rem] font-[700]"
                            ref={swiftRef}
                          >
                            {copiedSwift ? "Copied!" : ibanAcc?.data?.swift}
                          </div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="21.13"
                            viewBox="0 0 23 27"
                            fill="none"
                            onClick={copySwiftToClipboard}
                            className="cursor-pointer"
                          >
                            <path
                              d="M0 22.9736V7.84911C0.294512 5.94469 1.765 4.44749 3.70162 4.21367L14.4088 4.20898C16.3921 4.41989 17.9403 5.98999 18.1591 7.97722V22.7939C17.9751 24.8077 16.4045 26.3935 14.4088 26.6137H3.80013C1.91692 26.4877 0.208439 24.8473 0 22.9736ZM3.91835 6.30245C2.89015 6.44149 2.17928 7.2091 2.06728 8.23708V22.5856C2.18239 23.6563 2.94097 24.4187 4.00702 24.5349H14.1495C15.2156 24.4192 15.9741 23.6568 16.0892 22.5856V8.23708C15.9741 7.16639 15.2156 6.404 14.1495 6.28787L3.91783 6.30245H3.91835Z"
                              fill="#222B45"
                            />
                            <path
                              d="M18.6746 0C20.5713 0.294751 22.0573 1.77528 22.2927 3.71928L22.2974 19.9863C22.0817 21.2002 20.4406 21.2002 20.2249 19.9863L20.2285 4.02653C20.1129 2.9548 19.3543 2.19345 18.2888 2.07732L6.45181 2.06795C5.7902 1.89557 5.42517 1.18525 5.75079 0.55357C5.90012 0.263506 6.13604 0.138002 6.41552 0H18.6756L18.6746 0Z"
                              fill="#222B45"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="text-[#CFCFCF] font-satoshi text-[1.2rem] w-full pt-[1rem] font-[500] text-center flex justify-start items-center gap-2">
                      Bank Name and Address
                    </div>

                    <div className="flex w-full justify-start items-center gap-2">
                      <div className="text-[#202A43] font-satoshi text-[1.1875rem] font-[700] flex flex-col items-start">
                        <div>
                          {copiedNameAddress ? "" : ibanAcc?.data?.name}
                        </div>
                        <div ref={nameAddressRef} className="text-left">
                          {copiedNameAddress
                            ? "Copied!"
                            : ibanAcc?.data?.address}
                        </div>
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="21.13"
                        viewBox="0 0 23 27"
                        fill="none"
                        onClick={copyNameAddressToClipboard}
                        className="cursor-pointer"
                      >
                        <path
                          d="M0 22.9736V7.84911C0.294512 5.94469 1.765 4.44749 3.70162 4.21367L14.4088 4.20898C16.3921 4.41989 17.9403 5.98999 18.1591 7.97722V22.7939C17.9751 24.8077 16.4045 26.3935 14.4088 26.6137H3.80013C1.91692 26.4877 0.208439 24.8473 0 22.9736ZM3.91835 6.30245C2.89015 6.44149 2.17928 7.2091 2.06728 8.23708V22.5856C2.18239 23.6563 2.94097 24.4187 4.00702 24.5349H14.1495C15.2156 24.4192 15.9741 23.6568 16.0892 22.5856V8.23708C15.9741 7.16639 15.2156 6.404 14.1495 6.28787L3.91783 6.30245H3.91835Z"
                          fill="#222B45"
                        />
                        <path
                          d="M18.6746 0C20.5713 0.294751 22.0573 1.77528 22.2927 3.71928L22.2974 19.9863C22.0817 21.2002 20.4406 21.2002 20.2249 19.9863L20.2285 4.02653C20.1129 2.9548 19.3543 2.19345 18.2888 2.07732L6.45181 2.06795C5.7902 1.89557 5.42517 1.18525 5.75079 0.55357C5.90012 0.263506 6.13604 0.138002 6.41552 0H18.6756L18.6746 0Z"
                          fill="#222B45"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <ButtonComponent
                  title="Next step"
                  actionCallback={
                    paymentMethod == "card" &&
                    cardNumber !== "" &&
                    cvv != "" &&
                    expiryDate !== ""
                      ? () => {
                          const data: CardPayment = {
                            payment_channel: "",
                            transaction_id: "",
                            card: {
                              number: "",
                              expiry_date: "",
                              cvv: "",
                            },
                          };
                          handlePostCardPayment(data);
                        }
                      : paymentMethod == "card" &&
                        (cardNumber == "" || cvv == "" || expiryDate == "")
                      ? () =>
                          alert("Please, fill all your data before submitting.")
                      : () => setExchangeStep(3)
                  }
                  name="button--withSimbol w-fit mt-[4rem] flex gap-2 font-satoshi"
                  plusComponent={true}
                />
              </div>
            ) : null}

            {exchangeStep === 3 ? (
              <div>
                <div className="w-full items-center flex justify-center pt-[3rem]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="86"
                    height="129"
                    viewBox="0 0 86 129"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_3308_8423)">
                      <path
                        d="M55.8233 59.4141C53.8082 61.5311 53.0487 64.5741 54.2431 67.3269C55.658 70.5858 63.2529 75.178 66.2724 77.5605C78.7122 87.362 84.1572 98.3733 85.6211 114.242C85.9028 117.285 86.4602 120.464 85.2536 123.396C83.9735 126.494 80.9845 128.698 77.6648 129.006H8.1592C2.3589 128.025 -0.20744 123.717 0.0130569 118.007C0.625549 102.317 5.89298 89.0162 17.9836 78.9431C21.487 76.0236 29.431 71.4253 31.4645 67.8022C33.6817 63.8519 31.5564 60.5744 28.5797 57.9759C25.9031 55.6367 22.5711 53.6677 19.7475 51.4457C7.31396 41.6874 1.85666 30.5958 0.398927 14.7578C0.123305 11.7643 -0.421813 8.64115 0.705173 5.74636C1.94241 2.58 4.99874 0.308612 8.3552 0H77.8608C81.1438 0.419713 84.096 2.62938 85.3148 5.74636C86.4418 8.64115 85.8967 11.7643 85.6211 14.7578C84.1633 30.6082 78.6999 41.6688 66.2724 51.4395C63.0569 53.9701 58.5673 56.5255 55.8233 59.4079V59.4141ZM42.1709 89.0717C41.0929 89.2569 39.6658 90.337 38.7899 91.0036C29.9272 97.7746 21.8361 105.675 12.9734 112.465C11.0073 114.014 11.5095 117.094 14.0514 117.52H71.9747C74.7371 116.81 74.9392 114.217 72.9486 112.366C71.4909 111.008 69.5922 109.712 68.0425 108.409C60.76 102.299 53.4652 96.1945 46.146 90.1148C44.8413 89.3742 43.7327 88.7878 42.1709 89.0594V89.0717Z"
                        fill="#D2D2D2"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3308_8423">
                        <rect width="86" height="129" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>

                <div className=" w-full text-[#202A43] font-satoshi text-[1.1875rem] pt-[1rem] font-[500] text-center">
                  Transaction is in pending
                </div>

                <div className="w-full flex justify-center items-center">
                  <ButtonComponent
                    title="Done"
                    actionCallback={() => {
                      setExchangeModal(false);
                    }}
                    name="button--classic w-fit mt-[4rem] font-satoshi"
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  ) : (
    <div
      className="exchange-phone overflow-hidden min-h-screen relative w-full font-[helvetica] flex flex-col justify-between items-center py-[2rem] bg-[#1C1C1C] bg-cover bg-center"
      style={{
        backgroundImage: `url(${bgSNS})`,
      }}
    >
      <div className="exchange-phone__right">
        <div className="exchange-phone__right__top">
          <div className="exchange-phone__right__top__title !text-white !font-[helvetica] !text-[3.75rem] !font-[600] flex flex-col justify-center">
            <button
              className="mr-[18rem]"
              onClick={() => navigate("/pos/new-client-pos-sns")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="26"
                viewBox="0 0 16 26"
                fill="none"
              >
                <path
                  d="M13.5 2L3 14L13.5 24"
                  stroke="#8AFEFD"
                  stroke-width="4"
                  stroke-linecap="round"
                />
              </svg>
            </button>

            {/* <GoBack
              className={"w-full flex justify-start items-center mr-[3rem]"}
            />{" "} */}
            <div className="ml-[3rem]">Exchange</div>
          </div>
          <div className="exchange-phone__right__top__account !text-white text-[inter]">
            <div>{fullName}</div>
          </div>
        </div>

        <div className="exchange-phone__right__container">
          <div className="!bg-transparent exchange-phone__right__container--white">
            <div className="w-full flex-col justify-center items-center">
              <div className="exchange-phone__right__container--spacing">
                <CurrenciesSelector
                  balance={balance?.data ?? null}
                  isLoading={createExchangeMutation.isPending}
                  index={1}
                  currency={
                    !isRotated
                      ? selectedOptions.down.code
                      : selectedOptions.up.code
                  }
                  isUp={isRotated}
                  hasError={hasError}
                  value={
                    !isRotated
                      ? Number(selectedOptions.down.price.toFixed(8))
                      : selectedOptions.up.price
                  }
                  onChangeInput={onChangeInput}
                  options={options ?? []}
                  defaultOption={options?.[1] ?? CURRENCIES[3]}
                  currencie={""}
                  name={`bg-[#383838] !text-white line--pink selector ${
                    isRotated ? "up1" : "down1"
                  }`}
                  editable={true}
                  onChooseOption={handleOnChangeCurrencies}
                />

                <CurrenciesSelector
                  balance={balance?.data ?? null}
                  isLoading={createExchangeMutation.isPending}
                  index={0}
                  isUp={!isRotated}
                  hasError={hasError}
                  onChangeInput={onChangeInput}
                  options={options ?? []}
                  defaultOption={options?.[0] ?? CURRENCIES[2]}
                  currency={
                    !isRotated
                      ? selectedOptions.up.code
                      : selectedOptions.down.code
                  }
                  value={
                    !isRotated
                      ? selectedOptions.up.price
                      : Number(selectedOptions.down.price.toFixed(8))
                  }
                  currencie={""}
                  name={`bg-[#383838] !text-[#fff] line--bej selector ${
                    isRotated ? "down2" : "up2"
                  }`}
                  editable={true}
                  onChooseOption={handleOnChangeCurrencies}
                />

                <div className="absoluteCenter" onClick={handleClick}>
                  <img
                    className={isRotated ? "rotate" : "reverseRotate"}
                    src={ArrowSNS || "/placeholder.svg"}
                    alt="customArrow"
                  />
                </div>
              </div>

              <div className="exchange-phone__right__container__stats !mt-[4rem] !text-[#686868] !font-[helvetica]">
                <div
                  className={`${
                    isSNS()
                      ? "text-[#686868]"
                      : exchangeData
                      ? "text-[#222B45]"
                      : ""
                  } flex-space-100`}
                >
                  <div>Amount</div>
                  <div>
                    {exchangeData === null
                      ? "- -"
                      : `${selectedOptions.up.price} ${selectedOptions.up.code}`}
                  </div>
                </div>

                <div
                  className={`${
                    isSNS()
                      ? "text-[#686868]"
                      : exchangeData
                      ? "text-[#222B45]"
                      : ""
                  } flex-space-100`}
                >
                  <div>Price</div>
                  <div>
                    {exchangeData === null
                      ? "- -"
                      : `1 ${selectedOptions.up.code} = ${Number(
                          exchangeData?.rate ?? 0
                        ).toFixed(10)} ${selectedOptions.down.code}`}
                  </div>
                </div>

                <div
                  className={`${
                    isSNS()
                      ? "text-[#686868]"
                      : exchangeData
                      ? "text-[#222B45]"
                      : ""
                  } flex-space-100`}
                >
                  <div>Exchange</div>
                  <div>
                    {exchangeData === null
                      ? "- -"
                      : `${Number(selectedOptions.down.price ?? 0).toFixed(
                          10
                        )} ${selectedOptions.down.code}`}
                  </div>
                </div>

                <div
                  className={`${
                    isSNS()
                      ? "text-[#686868]"
                      : exchangeData
                      ? "text-[#222B45]"
                      : ""
                  } flex-space-100`}
                >
                  <div>Fee</div>
                  <div>
                    {exchangeData === null
                      ? "- -"
                      : `${Number(
                          exchangeData?.fee.amount_percentage ?? 0
                        ).toFixed(2)}%`}
                  </div>
                </div>

                <div
                  className={`${
                    isSNS()
                      ? "text-[#686868]"
                      : exchangeData
                      ? "text-[#222B45]"
                      : ""
                  } flex-space-100`}
                >
                  <div className="font-[700]">Estimated Total</div>
                  <div>
                    {exchangeData === null ? "- -" : exchangeData?.total}
                  </div>
                </div>
              </div>

              <div className="exchange-phone__right__container__button">
                <div className={`${!err ? "hidden" : ""} error text-red-500`}>
                  {err?.message ? err.message : "Something went wrong"}
                </div>

                <SwipeToAccept
                  onAccept={handleSwipeAccept}
                  // timeLeft={timeLeft}
                  disabled={
                    !exchangeData || createTransactionMutation.isPending
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {exchangeModal ? (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[999999999] h-screen min-h-full">
          <div className="min-w-[30rem] h-auto pb-[2rem] bg-white rounded-[1.5rem] border text-center relative px-8 py-8">
            {exchangeStep === 1 ? (
              <div className="flex flex-col justify-center items-center">
                {" "}
                <div className="text-[#05004E] pt-[2.5rem] text-center text-[1.5rem] font-[700]">
                  Confirm Exchange
                </div>
                <div className="text-[#737791] text-[1.2rem] w-full pt-[1rem] font-[500] text-center flex justify-center items-center gap-2">
                  The price will be recalculated in{" "}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_2348_4001)">
                      <path
                        d="M9.5 0C4.25326 0 0 4.25355 0 9.50018C0 14.7468 4.25326 19 9.5 19C14.7467 19 19 14.7468 19 9.50018C19 4.25355 14.7467 0 9.5 0ZM13.9057 12.3941C13.6774 12.7417 13.2977 12.9302 12.9108 12.9302C12.6868 12.9302 12.4604 12.8671 12.2594 12.7347L8.31104 10.1417V4.0143C8.31104 3.35747 8.84352 2.82536 9.5 2.82536C10.1565 2.82536 10.689 3.35747 10.689 4.0143V8.85867L13.5647 10.7475C14.1136 11.1082 14.266 11.8452 13.9057 12.3941Z"
                        fill="#737791"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_2348_4001">
                        <rect width="19" height="19" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>{" "}
                  00:
                  {timeLeft < 10 ? `0${timeLeft}` : timeLeft}
                </div>
                <ButtonComponent
                  title={
                    createTransactionMutation.isPending
                      ? "Loading..."
                      : "Accept & Pay"
                  }
                  actionCallback={() => {
                    if (!exchangeData) return;

                    handleCreateTransaction({
                      rate_id: exchangeData?.id,
                      amount: selectedOptions.up.price,
                      buy_bank_account_id: selectedAccounts.buy_bank_account,
                      buy_crypto_account_id:
                        selectedAccounts.buy_crypto_account,
                      sell_bank_account_id:
                        selectedAccounts.sell_bank_account,
                      sell_crypto_account_id:
                        selectedAccounts.sell_crypto_account,
                      client_id: clientData?.client_id ?? null,
                    });
                    setSelectedAccounts({
                      buy_bank_account: null,
                      buy_crypto_account: null,
                      sell_bank_account: null,
                      sell_crypto_account: null,
                    });
                  }}
                  name="button--classic w-fit mt-[4rem]"
                />
                <div
                  className="flex justify-center items-center gap-2 mt-4 cursor-pointer text-[#F00]"
                  onClick={() => setExchangeModal(false)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                  >
                    <path
                      d="M15.8026 14.0668C16.4956 15.2312 15.2312 16.4956 14.0668 15.8026L9.53101 11.2822L9.4217 11.3148L4.93315 15.8026C3.76877 16.4956 2.50437 15.2312 3.19743 14.0668L7.71776 9.53101L7.6852 9.4217L3.34782 5.09207C2.65244 4.37654 3.11448 3.17882 4.06335 3.01835C4.4153 2.95866 4.78043 3.08889 5.05099 3.31138L9.46899 7.71776L9.5783 7.68598L14.0668 3.19743C15.2312 2.50437 16.4956 3.76877 15.8026 4.93315L11.2822 9.46899L11.314 9.5783L15.8026 14.0668Z"
                      fill="#FF0000"
                    />
                  </svg>
                  Close transaction
                </div>{" "}
              </div>
            ) : null}

            {exchangeStep === 2 ? (
              <div className="flex flex-col justify-center items-center">
                <div className="flex gap-4">
                  <div
                    onClick={() => setPaymentMethod("card")}
                    className="flex flex-col justify-start items-start px-8 py-8 w-full border border-[#202A43] rounded-[1.5rem] relative"
                  >
                    {paymentMethod == "bank" && (
                      <div className="absolute w-full h-full top-0 left-0 bg-[#fff] opacity-75 rounded-[1.5rem]"></div>
                    )}

                    <div className="flex justify-between items-center w-full">
                      <div className="text-[#202A43] font-satoshi text-[1.5rem] font-bold leading-normal">
                        Pay by Card
                      </div>
                      <div className="border border-[#C0C0C0] rounded-[50%] px-[2px] py-[2px]">
                        <div
                          className={`w-4 h-4 rounded-[50%] ${
                            paymentMethod == "card" ? "bg-[#202A43]" : ""
                          } `}
                        ></div>
                      </div>
                    </div>

                    <form
                      className="h-full flex flex-col justify-center space-y-4"
                      autoComplete="on"
                    >
                      <div>
                        <input
                          type="text"
                          placeholder="Card Number"
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          autoComplete="cc-number"
                          value={cardNumber}
                          onChange={(e) => setCardNumber(e.target.value)}
                          required={paymentMethod == "card"}
                        />
                      </div>
                      <div>
                        <input
                          type="text"
                          placeholder="Name"
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          autoComplete="cc-name"
                          required={paymentMethod == "card"}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <input
                          type="text"
                          placeholder="MM/YY"
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          autoComplete="cc-exp"
                          pattern="^(0[1-9]|1[0-2])\/[0-9]{2}$"
                          maxLength={5}
                          value={expiryDate}
                          onChange={(e) => {
                            const value = e.target.value.replace(
                              /[^0-9/]/g,
                              ""
                            );
                            if (value.length === 2 && !value.includes("/")) {
                              setExpiryDate(value + "/");
                            } else {
                              setExpiryDate(value);
                            }
                          }}
                          required={paymentMethod == "card"}
                        />
                        <input
                          type="text"
                          placeholder="CVV"
                          className="w-full p-3 border border-gray-300 rounded-lg"
                          autoComplete="cc-csc"
                          value={cvv}
                          onChange={(e) => setCvv(e.target.value)}
                          required={paymentMethod == "card"}
                        />
                      </div>
                    </form>
                  </div>

                  <div
                    onClick={() => setPaymentMethod("bank")}
                    className="flex flex-col justify-center items-start px-8 py-8 w-full border border-[#202A43] rounded-[1.5rem] relative"
                  >
                    {paymentMethod == "card" && (
                      <div className="absolute w-full h-full top-0 left-0 bg-[#fff] opacity-75 rounded-[1.5rem]"></div>
                    )}

                    <div className="flex justify-between items-center w-full  pb-[2rem]">
                      <div className="text-[#202A43] font-satoshi text-[1.5rem] font-bold leading-normal">
                        Bank Transfer
                      </div>
                      <div className="border border-[#C0C0C0] rounded-[50%] px-[2px] py-[2px]">
                        <div
                          className={`w-4 h-4 rounded-[50%] ${
                            paymentMethod == "bank" ? "bg-[#202A43]" : ""
                          } `}
                        ></div>
                      </div>
                    </div>

                    <QRCodeCanvas
                      value={ibanAcc?.data?.iban || ""}
                      size={180}
                    />

                    <div className="flex justify-start pt-8">
                      <div>
                        <div className="text-[#CFCFCF] font-satoshi text-[1.2rem] w-full pt-[1rem] font-[500] text-center flex justify-start items-center gap-2 ">
                          IBAN
                        </div>
                        <div className="flex justify-start items-center gap-2 w-full ">
                          <div
                            className="text-[#202A43] text-[1.1875rem] font-[700]  font-satoshi"
                            ref={ibanRef}
                          >
                            {copiedIban ? "Copied!" : ibanAcc?.data?.iban}
                          </div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="21.13"
                            viewBox="0 0 23 27"
                            fill="none"
                            onClick={copyIbanToClipboard}
                            className="cursor-pointer"
                          >
                            <path
                              d="M0 22.9736V7.84911C0.294512 5.94469 1.765 4.44749 3.70162 4.21367L14.4088 4.20898C16.3921 4.41989 17.9403 5.98999 18.1591 7.97722V22.7939C17.9751 24.8077 16.4045 26.3935 14.4088 26.6137H3.80013C1.91692 26.4877 0.208439 24.8473 0 22.9736ZM3.91835 6.30245C2.89015 6.44149 2.17928 7.2091 2.06728 8.23708V22.5856C2.18239 23.6563 2.94097 24.4187 4.00702 24.5349H14.1495C15.2156 24.4192 15.9741 23.6568 16.0892 22.5856V8.23708C15.9741 7.16639 15.2156 6.404 14.1495 6.28787L3.91783 6.30245H3.91835Z"
                              fill="#222B45"
                            />
                            <path
                              d="M18.6746 0C20.5713 0.294751 22.0573 1.77528 22.2927 3.71928L22.2974 19.9863C22.0817 21.2002 20.4406 21.2002 20.2249 19.9863L20.2285 4.02653C20.1129 2.9548 19.3543 2.19345 18.2888 2.07732L6.45181 2.06795C5.7902 1.89557 5.42517 1.18525 5.75079 0.55357C5.90012 0.263506 6.13604 0.138002 6.41552 0H18.6756L18.6746 0Z"
                              fill="#222B45"
                            />
                          </svg>
                        </div>
                      </div>
                      <div>
                        <div className="text-[#CFCFCF] font-satoshi text-[1.2rem] w-full pt-[1rem] font-[500] text-center flex justify-start ps-[3rem] items-center gap-2">
                          SWIFT
                        </div>
                        <div className="flex w-full justify-start ps-[3rem] items-center gap-2">
                          <div
                            className="text-[#202A43] font-satoshi text-[1.1875rem] font-[700]"
                            ref={swiftRef}
                          >
                            {copiedSwift ? "Copied!" : ibanAcc?.data?.swift}
                          </div>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="21.13"
                            viewBox="0 0 23 27"
                            fill="none"
                            onClick={copySwiftToClipboard}
                            className="cursor-pointer"
                          >
                            <path
                              d="M0 22.9736V7.84911C0.294512 5.94469 1.765 4.44749 3.70162 4.21367L14.4088 4.20898C16.3921 4.41989 17.9403 5.98999 18.1591 7.97722V22.7939C17.9751 24.8077 16.4045 26.3935 14.4088 26.6137H3.80013C1.91692 26.4877 0.208439 24.8473 0 22.9736ZM3.91835 6.30245C2.89015 6.44149 2.17928 7.2091 2.06728 8.23708V22.5856C2.18239 23.6563 2.94097 24.4187 4.00702 24.5349H14.1495C15.2156 24.4192 15.9741 23.6568 16.0892 22.5856V8.23708C15.9741 7.16639 15.2156 6.404 14.1495 6.28787L3.91783 6.30245H3.91835Z"
                              fill="#222B45"
                            />
                            <path
                              d="M18.6746 0C20.5713 0.294751 22.0573 1.77528 22.2927 3.71928L22.2974 19.9863C22.0817 21.2002 20.4406 21.2002 20.2249 19.9863L20.2285 4.02653C20.1129 2.9548 19.3543 2.19345 18.2888 2.07732L6.45181 2.06795C5.7902 1.89557 5.42517 1.18525 5.75079 0.55357C5.90012 0.263506 6.13604 0.138002 6.41552 0H18.6756L18.6746 0Z"
                              fill="#222B45"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div className="text-[#CFCFCF] font-satoshi text-[1.2rem] w-full pt-[1rem] font-[500] text-center flex justify-start items-center gap-2">
                      Bank Name and Address
                    </div>

                    <div className="flex w-full justify-start items-center gap-2">
                      <div className="text-[#202A43] font-satoshi text-[1.1875rem] font-[700] flex flex-col items-start">
                        <div>
                          {copiedNameAddress ? "" : ibanAcc?.data?.name}
                        </div>
                        <div ref={nameAddressRef} className="text-left">
                          {copiedNameAddress
                            ? "Copied!"
                            : ibanAcc?.data?.address}
                        </div>
                      </div>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="21.13"
                        viewBox="0 0 23 27"
                        fill="none"
                        onClick={copyNameAddressToClipboard}
                        className="cursor-pointer"
                      >
                        <path
                          d="M0 22.9736V7.84911C0.294512 5.94469 1.765 4.44749 3.70162 4.21367L14.4088 4.20898C16.3921 4.41989 17.9403 5.98999 18.1591 7.97722V22.7939C17.9751 24.8077 16.4045 26.3935 14.4088 26.6137H3.80013C1.91692 26.4877 0.208439 24.8473 0 22.9736ZM3.91835 6.30245C2.89015 6.44149 2.17928 7.2091 2.06728 8.23708V22.5856C2.18239 23.6563 2.94097 24.4187 4.00702 24.5349H14.1495C15.2156 24.4192 15.9741 23.6568 16.0892 22.5856V8.23708C15.9741 7.16639 15.2156 6.404 14.1495 6.28787L3.91783 6.30245H3.91835Z"
                          fill="#222B45"
                        />
                        <path
                          d="M18.6746 0C20.5713 0.294751 22.0573 1.77528 22.2927 3.71928L22.2974 19.9863C22.0817 21.2002 20.4406 21.2002 20.2249 19.9863L20.2285 4.02653C20.1129 2.9548 19.3543 2.19345 18.2888 2.07732L6.45181 2.06795C5.7902 1.89557 5.42517 1.18525 5.75079 0.55357C5.90012 0.263506 6.13604 0.138002 6.41552 0H18.6756L18.6746 0Z"
                          fill="#222B45"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                <ButtonComponent
                  title="Next step"
                  actionCallback={
                    paymentMethod == "card" &&
                    cardNumber !== "" &&
                    cvv != "" &&
                    expiryDate !== ""
                      ? () => {
                          const data: CardPayment = {
                            payment_channel: "",
                            transaction_id: "",
                            card: {
                              number: "",
                              expiry_date: "",
                              cvv: "",
                            },
                          };
                          handlePostCardPayment(data);
                        }
                      : paymentMethod == "card" &&
                        (cardNumber == "" || cvv == "" || expiryDate == "")
                      ? () =>
                          alert("Please, fill all your data before submitting.")
                      : () => setExchangeStep(3)
                  }
                  name="button--withSimbol w-fit mt-[4rem] flex gap-2 font-satoshi"
                  plusComponent={true}
                />
              </div>
            ) : null}

            {exchangeStep === 3 ? (
              <div>
                <div className="w-full items-center flex justify-center pt-[3rem]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="86"
                    height="129"
                    viewBox="0 0 86 129"
                    fill="none"
                  >
                    <g clipPath="url(#clip0_3308_8423)">
                      <path
                        d="M55.8233 59.4141C53.8082 61.5311 53.0487 64.5741 54.2431 67.3269C55.658 70.5858 63.2529 75.178 66.2724 77.5605C78.7122 87.362 84.1572 98.3733 85.6211 114.242C85.9028 117.285 86.4602 120.464 85.2536 123.396C83.9735 126.494 80.9845 128.698 77.6648 129.006H8.1592C2.3589 128.025 -0.20744 123.717 0.0130569 118.007C0.625549 102.317 5.89298 89.0162 17.9836 78.9431C21.487 76.0236 29.431 71.4253 31.4645 67.8022C33.6817 63.8519 31.5564 60.5744 28.5797 57.9759C25.9031 55.6367 22.5711 53.6677 19.7475 51.4457C7.31396 41.6874 1.85666 30.5958 0.398927 14.7578C0.123305 11.7643 -0.421813 8.64115 0.705173 5.74636C1.94241 2.58 4.99874 0.308612 8.3552 0H77.8608C81.1438 0.419713 84.096 2.62938 85.3148 5.74636C86.4418 8.64115 85.8967 11.7643 85.6211 14.7578C84.1633 30.6082 78.6999 41.6688 66.2724 51.4395C63.0569 53.9701 58.5673 56.5255 55.8233 59.4079V59.4141ZM42.1709 89.0717C41.0929 89.2569 39.6658 90.337 38.7899 91.0036C29.9272 97.7746 21.8361 105.675 12.9734 112.465C11.0073 114.014 11.5095 117.094 14.0514 117.52H71.9747C74.7371 116.81 74.9392 114.217 72.9486 112.366C71.4909 111.008 69.5922 109.712 68.0425 108.409C60.76 102.299 53.4652 96.1945 46.146 90.1148C44.8413 89.3742 43.7327 88.7878 42.1709 89.0594V89.0717Z"
                        fill="#D2D2D2"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_3308_8423">
                        <rect width="86" height="129" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                </div>

                <div className=" w-full text-[#202A43] font-satoshi text-[1.1875rem] pt-[1rem] font-[500] text-center">
                  Transaction is in pending
                </div>

                <div className="w-full flex justify-center items-center">
                  <ButtonComponent
                    title="Done"
                    actionCallback={() => {
                      setExchangeModal(false);
                    }}
                    name="button--classic w-fit mt-[4rem] font-satoshi"
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ExchangePhone;
